import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsService } from '../services/products.service';
import { ArrowLeft, Plus, Pencil, Trash2, X, GripVertical, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { PlansTab } from '../components/config/PlansTab';
import { SyncConfigTab } from '../components/config/SyncConfigTab';
import { BillingAccountingTab } from '../components/config/BillingAccountingTab';

type StageType = 'OPEN' | 'WON' | 'LOST';

interface Stage {
  id: number;
  name: string;
  order: number;
  type: StageType;
  color: string;
  showDocugenStage: boolean;
}

interface Origin {
  id: number;
  name: string;
  active: boolean;
}

const STAGE_TYPE_LABELS: Record<StageType, string> = {
  OPEN: 'Aberto',
  WON: 'Ganho',
  LOST: 'Perdido',
};

const STAGE_TYPE_COLORS: Record<StageType, string> = {
  OPEN: 'bg-subtle text-info',
  WON: 'bg-subtle text-accent',
  LOST: 'bg-subtle text-danger',
};

const STAGE_TYPES: StageType[] = ['OPEN', 'WON', 'LOST'];

function StageModal({
  stage,
  productId,
  onClose,
}: {
  stage: Stage | null;
  productId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!stage;
  const [form, setForm] = useState({
    name: stage?.name ?? '',
    order: stage?.order ?? 1,
    type: stage?.type ?? ('OPEN' as StageType),
    color: stage?.color ?? '#3b82f6',
    showDocugenStage:
      stage?.showDocugenStage ?? (stage?.type === 'WON' || stage?.type === 'LOST' || false),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => productsService.createStage(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stages', productId] });
      toast.success('Etapa criada!');
      onClose();
    },
    onError: () => toast.error('Erro ao criar etapa'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => productsService.updateStage(productId, stage!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stages', productId] });
      toast.success('Etapa atualizada!');
      onClose();
    },
    onError: () => toast.error('Erro ao atualizar etapa'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(form);
    } else {
      createMutation.mutate(form);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{isEdit ? 'Editar Etapa' : 'Nova Etapa'}</h3>
          <button onClick={onClose} className="text-ink-tertiary hover:text-ink-primary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Nome</label>
            <input
              className="input w-full"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label">Ordem</label>
            <input
              type="number"
              min={1}
              className="input w-full"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              required
            />
          </div>
          <div>
            <label className="label">Tipo</label>
            <select
              className="input w-full"
              value={form.type}
              onChange={(e) => {
                const newType = e.target.value as StageType;
                setForm({
                  ...form,
                  type: newType,
                  showDocugenStage:
                    newType === 'WON' || newType === 'LOST' ? true : form.showDocugenStage,
                });
              }}
            >
              {STAGE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {STAGE_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Cor</label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer border border-border-strong"
              />
              <input
                type="text"
                className="input flex-1"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#3b82f6"
                pattern="^#[0-9a-fA-F]{6}$"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showDocugenStage"
              checked={form.showDocugenStage}
              onChange={(e) => setForm({ ...form, showDocugenStage: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="showDocugenStage" className="text-sm font-medium text-ink-secondary">
              Mostrar engajamento Docugen
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? <LoadingSpinner size="sm" /> : isEdit ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ProductConfigPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const productId = Number(id);
  const [activeTab, setActiveTab] = useState<'stages' | 'origins' | 'plans' | 'sync' | 'billing'>(
    'stages'
  );
  const [stageModal, setStageModal] = useState<Stage | null | 'new'>(null);
  const [newOriginName, setNewOriginName] = useState('');
  const [showOriginInput, setShowOriginInput] = useState(false);
  const [editingOrigin, setEditingOrigin] = useState<{ id: number; name: string } | null>(null);

  const { data: productData, isLoading: productLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => productsService.getById(productId),
    enabled: !!productId,
  });

  const { data: stagesData, isLoading: stagesLoading } = useQuery({
    queryKey: ['stages', productId],
    queryFn: () => productsService.getStages(productId),
    enabled: !!productId,
  });

  const { data: originsData, isLoading: originsLoading } = useQuery({
    queryKey: ['origins', productId],
    queryFn: () => productsService.getOrigins(productId),
    enabled: !!productId,
  });

  const deleteStageMutation = useMutation({
    mutationFn: (stageId: number) => productsService.deleteStage(productId, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stages', productId] });
      toast.success('Etapa removida!');
    },
    onError: () => toast.error('Erro ao remover etapa'),
  });

  const createOriginMutation = useMutation({
    mutationFn: (name: string) => productsService.createOrigin(productId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['origins', productId] });
      setNewOriginName('');
      setShowOriginInput(false);
      toast.success('Origem criada!');
    },
    onError: () => toast.error('Erro ao criar origem'),
  });

  const updateOriginMutation = useMutation({
    mutationFn: ({ originId, data }: { originId: number; data: any }) =>
      productsService.updateOrigin(productId, originId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['origins', productId] });
      setEditingOrigin(null);
      toast.success('Origem atualizada!');
    },
    onError: () => toast.error('Erro ao atualizar origem'),
  });

  const stages: Stage[] = stagesData?.data || stagesData || [];
  const origins: Origin[] = originsData?.data || originsData || [];

  const product = productData?.data || productData;

  if (productLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/config/produto')}
          className="flex items-center gap-2 text-sm text-ink-secondary hover:text-ink-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <div>
          <h1 className="text-2xl font-bold text-ink-primary">{product?.name ?? 'Produto'}</h1>
          {/* Slug e id no cabeçalho: dois produtos podem ter o mesmo nome, e sem
              isto não há como saber qual deles está aberto. */}
          <p className="text-ink-secondary text-sm">
            Configuração do produto
            {product?.slug ? (
              <>
                {' · '}
                <code className="bg-canvas px-1 rounded">{product.slug}</code>
                {` · id ${productId}`}
              </>
            ) : null}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('stages')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'stages'
              ? 'border-ink-primary text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Etapas do Pipeline
        </button>
        <button
          onClick={() => setActiveTab('origins')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'origins'
              ? 'border-ink-primary text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Origens de Lead
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'plans'
              ? 'border-ink-primary text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Planos
        </button>
        <button
          onClick={() => setActiveTab('sync')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'sync'
              ? 'border-ink-primary text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Sync de Clientes
        </button>
        <button
          onClick={() => setActiveTab('billing')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'billing'
              ? 'border-ink-primary text-ink-primary'
              : 'border-transparent text-ink-secondary hover:text-ink-primary'
          }`}
        >
          Billing contábil
        </button>
      </div>

      {/* Stages Tab */}
      {activeTab === 'stages' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-tertiary">
              {stages.length} etapa{stages.length !== 1 ? 's' : ''} configurada
              {stages.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={() => setStageModal('new')}
              className="flex items-center gap-2 bg-ink-primary text-canvas px-3 py-1.5 rounded-lg hover:bg-ink-secondary text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Nova Etapa
            </button>
          </div>

          {stagesLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : stages.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-[14px] border border-border">
              <p className="text-ink-tertiary">Nenhuma etapa configurada.</p>
            </div>
          ) : (
            <div className="bg-surface rounded-[14px] border border-border divide-y divide-border">
              {[...stages]
                .sort((a, b) => a.order - b.order)
                .map((stage) => (
                  <div key={stage.id} className="flex items-center gap-4 px-5 py-4">
                    <GripVertical className="w-4 h-4 text-ink-tertiary flex-shrink-0" />
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink-primary">{stage.name}</span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_TYPE_COLORS[stage.type]}`}
                        >
                          {STAGE_TYPE_LABELS[stage.type]}
                        </span>
                        {stage.showDocugenStage && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-subtle text-accent">
                            Engajamento
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-ink-tertiary">Ordem: {stage.order}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setStageModal(stage)}
                        className="p-1.5 text-ink-tertiary hover:text-ink-primary hover:bg-subtle rounded transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Remover a etapa "${stage.name}"?`)) {
                            deleteStageMutation.mutate(stage.id);
                          }
                        }}
                        className="p-1.5 text-ink-tertiary hover:text-danger hover:bg-subtle rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Origins Tab */}
      {activeTab === 'origins' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-tertiary">
              {origins.length} origem{origins.length !== 1 ? 'ns' : ''} configurada
              {origins.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={() => setShowOriginInput(true)}
              className="flex items-center gap-2 bg-ink-primary text-canvas px-3 py-1.5 rounded-lg hover:bg-ink-secondary text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Nova Origem
            </button>
          </div>

          {showOriginInput && (
            <div className="bg-surface rounded-[14px] border border-border p-4">
              <div className="flex items-center gap-3">
                <input
                  autoFocus
                  className="input flex-1"
                  placeholder="Nome da origem (ex: Google Ads, Indicação...)"
                  value={newOriginName}
                  onChange={(e) => setNewOriginName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newOriginName.trim()) {
                      createOriginMutation.mutate(newOriginName.trim());
                    }
                    if (e.key === 'Escape') {
                      setShowOriginInput(false);
                      setNewOriginName('');
                    }
                  }}
                />
                <button
                  onClick={() => {
                    if (newOriginName.trim()) {
                      createOriginMutation.mutate(newOriginName.trim());
                    }
                  }}
                  disabled={!newOriginName.trim() || createOriginMutation.isPending}
                  className="btn-primary"
                >
                  {createOriginMutation.isPending ? <LoadingSpinner size="sm" /> : 'Adicionar'}
                </button>
                <button
                  onClick={() => {
                    setShowOriginInput(false);
                    setNewOriginName('');
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {originsLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : origins.length === 0 ? (
            <div className="text-center py-12 bg-surface rounded-[14px] border border-border">
              <Globe className="w-12 h-12 text-ink-tertiary mx-auto mb-3" />
              <p className="text-ink-tertiary">Nenhuma origem configurada.</p>
            </div>
          ) : (
            <div className="bg-surface rounded-[14px] border border-border divide-y divide-border">
              {origins.map((origin) => (
                <div key={origin.id} className="flex items-center gap-4 px-5 py-4">
                  {editingOrigin?.id === origin.id ? (
                    <>
                      <input
                        autoFocus
                        className="input flex-1"
                        value={editingOrigin.name}
                        onChange={(e) =>
                          setEditingOrigin({ ...editingOrigin, name: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateOriginMutation.mutate({
                              originId: origin.id,
                              data: { name: editingOrigin.name },
                            });
                          }
                          if (e.key === 'Escape') {
                            setEditingOrigin(null);
                          }
                        }}
                      />
                      <button
                        onClick={() =>
                          updateOriginMutation.mutate({
                            originId: origin.id,
                            data: { name: editingOrigin.name },
                          })
                        }
                        disabled={updateOriginMutation.isPending}
                        className="btn-primary text-sm"
                      >
                        Salvar
                      </button>
                      <button
                        onClick={() => setEditingOrigin(null)}
                        className="btn-secondary text-sm"
                      >
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-ink-primary">{origin.name}</span>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={origin.active}
                          onChange={(e) =>
                            updateOriginMutation.mutate({
                              originId: origin.id,
                              data: { active: e.target.checked },
                            })
                          }
                          className="rounded"
                        />
                        Ativo
                      </label>
                      <button
                        onClick={() => setEditingOrigin({ id: origin.id, name: origin.name })}
                        className="p-1.5 text-ink-tertiary hover:text-ink-primary hover:bg-subtle rounded transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && <PlansTab productId={productId} />}

      {/* Sync Config Tab */}
      {activeTab === 'sync' && <SyncConfigTab productId={productId} />}

      {/* Billing accounting configuration */}
      {activeTab === 'billing' && product && (
        <BillingAccountingTab productId={productId} productName={product.name} />
      )}

      {/* Email Marketing Automation */}
      {product && (
        <section className="mt-6 rounded border border-border bg-surface p-4">
          <h3 className="mb-2 text-lg font-semibold">Automação de Email Marketing</h3>
          <p className="mb-3 text-sm text-ink-secondary">
            Leads que receberem email marketing serão movidos para esta coluna. A automação{' '}
            <strong>nunca regride</strong> no funil: cards só movem de colunas anteriores ou iguais
            à coluna alvo.
          </p>
          <label className="block text-sm font-medium">Coluna alvo</label>
          <select
            className="mt-1 block w-full rounded border-border-strong text-sm"
            value={product.emailMarketingTargetStageId ?? ''}
            onChange={async (e) => {
              const raw = e.target.value;
              const nextId = raw === '' ? null : parseInt(raw, 10);
              await productsService.update(productId, { emailMarketingTargetStageId: nextId });
              queryClient.invalidateQueries({ queryKey: ['product', productId] });
            }}
          >
            <option value="">— desligado (não mover) —</option>
            {(stagesData?.data ?? stagesData ?? []).map(
              (s: { id: number; name: string; order: number }) => (
                <option key={s.id} value={s.id}>
                  {s.name} (order={s.order})
                </option>
              )
            )}
          </select>
        </section>
      )}

      {/* Stage Modal */}
      {stageModal !== null && (
        <StageModal
          stage={stageModal === 'new' ? null : stageModal}
          productId={productId}
          onClose={() => setStageModal(null)}
        />
      )}
    </div>
  );
}

export default ProductConfigPage;
